'use client';

/**
 * Card de "producto por asignación" para la grilla unificada de /reservas
 * (plan §10). Comparte primitives VISUALES con las cards nombradas
 * (ApartmentCardFrame, overlay, carrusel, bloque de precio) pero NO el modelo
 * de dominio. El clic abre la FICHA (/apartamentos/tipo/[slug]); NO abre el modal.
 *
 * Sin badges de precio-fijo, sin etiqueta de piso-por-asignar, sin disclaimer
 * de foto representativa, sin rating/reseñas heredadas ni número de apartamento
 * (auditoría/plan §12).
 */
import { useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import { Building2, Check, ChevronLeft, ChevronRight, Users, Video } from 'lucide-react';
import type { AssignmentListingDTO } from '@/lib/assignment/public-types';
import ApartmentCardFrame from '@/components/apartments/card/ApartmentCardFrame';
import ApartmentCardMediaOverlay from '@/components/apartments/card/ApartmentCardMediaOverlay';
import { formatMoney } from '@/components/apartments/card/apartment-card-utils';
import { getBlurDataUrl, versionedImageUrl } from '@/lib/image-version';
import { trackPostHogEvent, type CTAEventProps } from '@/lib/analytics/posthog';

interface Props {
  product: AssignmentListingDTO;
  arrival: string;
  departure: string;
  nights: number;
  guests: number;
  guestAdults: number;
  childrenAges: string[];
  source?: string;
  position: number;
  ctaAttribution: CTAEventProps;
  priority?: boolean;
}

function buildTipoUrl(slug: string, p: {
  arrival: string; departure: string; guests: number; guestAdults: number; childrenAges: string[]; source?: string;
}): string {
  const s = new URLSearchParams();
  if (p.arrival) s.set('checkIn', p.arrival);
  if (p.departure) s.set('checkOut', p.departure);
  if (p.guests) s.set('guests', String(p.guests));
  if (p.guestAdults) s.set('guestAdults', String(p.guestAdults));
  if (p.childrenAges.length) s.set('childrenAges', p.childrenAges.join(','));
  if (p.source) s.set('source', p.source);
  const qs = s.toString();
  return `/apartamentos/tipo/${slug}${qs ? `?${qs}` : ''}`;
}

function metaLine(product: AssignmentListingDTO): string {
  return `${product.bedroomsLabel} · hasta ${product.capacityPerUnit} personas · ${product.buildingName}`;
}

export default function AssignmentProductSearchCard({
  product, arrival, departure, nights, guests, guestAdults, childrenAges, source, position, ctaAttribution, priority = false,
}: Props) {
  const [imageIndex, setImageIndex] = useState(0);
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const images = product.previewMedia;
  const imageCount = images.length;
  const current = images[imageIndex];

  const detailUrl = useMemo(
    () => buildTipoUrl(product.slug, { arrival, departure, guests, guestAdults, childrenAges, source }),
    [product.slug, arrival, departure, guests, guestAdults, childrenAges, source]
  );

  const goToImage = (dir: -1 | 1) => {
    if (imageCount <= 1) return;
    setImageIndex((c) => (c + dir + imageCount) % imageCount);
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStart.current === null) return;
    const t = e.changedTouches[0];
    const dx = touchStart.current.x - (t?.clientX ?? 0);
    const dy = touchStart.current.y - (t?.clientY ?? 0);
    touchStart.current = null;
    if (Math.abs(dx) < 34 || Math.abs(dx) <= Math.abs(dy)) return;
    e.preventDefault();
    goToImage(dx > 0 ? 1 : -1);
  };

  return (
    <ApartmentCardFrame
      href={detailUrl}
      prefetch={false}
      dataApartmentId={`tipo-${product.slug}`}
      ariaLabel={`Ver detalles de ${product.displayName}, hasta ${product.capacityPerUnit} personas, ${formatMoney(product.totalPrice)} total`}
      onClick={() => {
        trackPostHogEvent('assignment_product_clicked', {
          ...ctaAttribution,
          product_slug: product.slug,
          search_position: position,
          has_video: product.hasVideo,
          result_kind: 'assignment',
          nights,
        });
      }}
      onTouchStart={(e) => {
        const t = e.changedTouches[0];
        touchStart.current = t ? { x: t.clientX, y: t.clientY } : null;
      }}
      onTouchEnd={handleTouchEnd}
    >
      {/* === ZONA VISUAL (carrusel) === */}
      <div className="relative aspect-[3/2] w-full overflow-hidden bg-stone-100 sm:aspect-[4/3]">
        {current ? (
          <Image
            src={versionedImageUrl(current.url)}
            alt={current.alt}
            fill
            sizes="(max-width: 640px) 95vw, (max-width: 1024px) 45vw, 380px"
            className="pointer-events-none object-cover transition-transform duration-700 group-hover:scale-105"
            priority={priority}
            loading={priority ? 'eager' : 'lazy'}
            quality={78}
            placeholder="blur"
            blurDataURL={getBlurDataUrl(versionedImageUrl(current.url))}
          />
        ) : (
          <div className="pointer-events-none flex h-full w-full items-center justify-center bg-stone-100 text-stone-500">
            <Building2 className="h-10 w-10" strokeWidth={1.5} />
          </div>
        )}

        <ApartmentCardMediaOverlay
          title={product.displayName}
          neighborhood={product.neighborhood}
          topRight={
            product.hasVideo ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-black/55 px-2 py-1 text-[11px] font-medium text-white backdrop-blur-sm">
                <Video className="h-3 w-3" strokeWidth={2.25} /> Video
              </span>
            ) : null
          }
        />

        {imageCount > 1 ? (
          <>
            <button
              type="button"
              aria-label="Foto anterior"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); goToImage(-1); }}
              className="absolute left-1 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center bg-transparent text-white drop-shadow-[0_2px_7px_rgba(0,0,0,0.75)] transition hover:text-white focus-visible:rounded-full focus-visible:bg-black/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80 sm:opacity-0 sm:group-hover:opacity-100 sm:focus-visible:opacity-100"
            >
              <ChevronLeft className="h-6 w-6" strokeWidth={2.35} />
            </button>
            <button
              type="button"
              aria-label="Siguiente foto"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); goToImage(1); }}
              className="absolute right-1 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center bg-transparent text-white drop-shadow-[0_2px_7px_rgba(0,0,0,0.75)] transition hover:text-white focus-visible:rounded-full focus-visible:bg-black/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80 sm:opacity-0 sm:group-hover:opacity-100 sm:focus-visible:opacity-100"
            >
              <ChevronRight className="h-6 w-6" strokeWidth={2.35} />
            </button>
            <span className="pointer-events-none absolute bottom-3 right-3 z-20 hidden rounded-full bg-black/45 px-2 py-0.5 text-[11px] font-medium text-white backdrop-blur-sm transition-opacity sm:block sm:opacity-0 sm:group-hover:opacity-100">
              {imageIndex + 1} / {imageCount}
            </span>
          </>
        ) : null}
      </div>

      {/* === ZONA INFO === */}
      <div className="p-4">
        <div className="mb-2 space-y-1">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-sm font-medium text-gray-600">
              Total por {nights} {nights === 1 ? 'noche' : 'noches'}:
            </span>
            <span className="text-lg font-semibold text-gray-900 tabular-nums">
              {formatMoney(product.totalPrice)}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-1.5 text-sm text-gray-600">
          <Users className="h-3.5 w-3.5 text-gray-400" strokeWidth={2} />
          <span>{metaLine(product)}</span>
        </div>

        {product.guaranteedFeatures.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-600">
            {product.guaranteedFeatures.map((feature) => (
              <span key={feature.key} className="inline-flex min-h-5 items-center gap-1">
                <Check className="h-3.5 w-3.5 text-emerald-700" strokeWidth={2.25} />
                {feature.label}
              </span>
            ))}
          </div>
        )}
      </div>
    </ApartmentCardFrame>
  );
}
